import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'flowbite-react';
import { HiDownload } from 'react-icons/hi';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable, type ColumnDef } from '../components/tables';
import { useAuth } from '../contexts/AuthContext';
import { getUserErrorMessage } from '../api';
import {
  organizationActivityApi,
  type ActivityStatus,
  type ActivityThresholds,
  type OrganizationActivitySummary,
} from '../api/activityLog';
import {
  STATUS_META,
  WINDOW_OPTIONS,
  calendarLabel,
  formatDateTime,
  formatRelative,
  statusLegend,
} from '../components/activity/activityDisplay';

const TYPE_LABELS: Record<string, string> = {
  iu: 'Intermediate Unit',
  district: 'District',
  ctc: 'CTC',
  jointure: 'Jointure',
  charter: 'Charter Network',
};

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * How current each organization's directory information is, across every
 * LEA. Super admins only. Each row links to that organization's Activity tab.
 */
export const DataHealthPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [rows, setRows] = useState<OrganizationActivitySummary[]>([]);
  const [thresholds, setThresholds] = useState<ActivityThresholds | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser && currentUser.role !== 'superAdmin') {
      navigate('/access-denied');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    organizationActivityApi.listSummaries(days)
      .then(result => {
        if (cancelled) return;
        setRows(result.data);
        setThresholds(result.thresholds);
        setError(null);
      })
      .catch(err => { if (!cancelled) setError(getUserErrorMessage(err, 'Failed to load data health')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const types = useMemo(() => Array.from(new Set(rows.map(r => r.leaType ?? ''))).filter(Boolean), [rows]);
  const visible = useMemo(
    () => rows.filter(r => (!typeFilter || r.leaType === typeFilter) && (!statusFilter || r.status === statusFilter)),
    [rows, typeFilter, statusFilter]
  );
  const statusCounts = useMemo(() => {
    const counts: Record<ActivityStatus, number> = { current: 0, aging: 0, stale: 0, never: 0 };
    rows.filter(r => !typeFilter || r.leaType === typeFilter).forEach(r => { counts[r.status] += 1; });
    return counts;
  }, [rows, typeFilter]);

  const exportCsv = () => {
    const header = ['Organization', 'Type', 'Status', 'Last edit by own users', 'Last edited by', 'Own-user edits',
      'AIU staff edits', 'Editors', 'Accounts signed in', 'Accounts', 'Never signed in', 'Contacts',
      'Without role tags', 'Not updated in a year', 'Calendar', 'Superintendent listed', 'Board members'];
    const lines = visible.map(r => [
      r.name, TYPE_LABELS[r.leaType ?? ''] ?? r.leaType, STATUS_META[r.status].label,
      formatDateTime(r.lastOrganizationEditAt), r.lastOrganizationEditBy, r.organizationEdits, r.aiuEdits,
      r.editors, r.signedInAccounts, r.accountCount, r.neverSignedIn, r.contacts, r.roleTagGaps,
      r.staleContacts, calendarLabel(r), r.hasSuperintendent ? 'Yes' : 'No', r.boardMembers,
    ].map(csvCell).join(','));
    const blob = new Blob(['﻿' + [header.map(csvCell).join(','), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `data-health-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<OrganizationActivitySummary>[] = [
    {
      id: 'name',
      header: 'Organization',
      accessor: 'name',
      sortable: true,
      searchable: true,
      nowrap: false,
      render: (_v, row) => (
        <div>
          <Link to={`/leas/${row.leaId}?tab=activity`} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            {row.name}
          </Link>
          <div className="text-xs text-gray-500 dark:text-gray-400">{TYPE_LABELS[row.leaType ?? ''] ?? row.leaType ?? ''}</div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => STATUS_META[row.status].rank,
      sortable: true,
      searchable: false,
      render: (_v, row) => (
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_META[row.status].className}`}>
          {STATUS_META[row.status].label}
        </span>
      ),
    },
    {
      id: 'lastEdit',
      header: 'Last edit by own users',
      accessor: (row) => row.lastOrganizationEditAt ?? '',
      sortable: true,
      searchable: false,
      nowrap: false,
      render: (_v, row) => (
        <div title={formatDateTime(row.lastOrganizationEditAt)}>
          {formatRelative(row.lastOrganizationEditAt)}
          {row.lastOrganizationEditBy && <div className="text-xs text-gray-500 dark:text-gray-400">{row.lastOrganizationEditBy}</div>}
        </div>
      ),
    },
    {
      id: 'edits',
      header: `Edits (own / AIU)`,
      accessor: 'organizationEdits',
      sortable: true,
      searchable: false,
      align: 'right',
      render: (_v, row) => <span>{row.organizationEdits} <span className="text-gray-400">/ {row.aiuEdits}</span></span>,
    },
    { id: 'editors', header: 'Editors', accessor: 'editors', sortable: true, searchable: false, align: 'right' },
    {
      id: 'signIns',
      header: 'Signed in',
      accessor: 'signedInAccounts',
      sortable: true,
      searchable: false,
      align: 'right',
      render: (_v, row) => (
        <span title={row.neverSignedIn ? `${row.neverSignedIn} never signed in` : undefined}>
          {row.signedInAccounts} <span className="text-gray-400">of {row.accountCount}</span>
        </span>
      ),
    },
    {
      id: 'gaps',
      header: 'No role tag',
      accessor: 'roleTagGaps',
      sortable: true,
      searchable: false,
      align: 'right',
      render: (_v, row) => (
        <span className={row.roleTagGaps ? 'font-medium text-amber-700 dark:text-amber-400' : ''}>
          {row.roleTagGaps} <span className="text-gray-400">of {row.contacts}</span>
        </span>
      ),
    },
    { id: 'stale', header: 'Not updated in a year', accessor: 'staleContacts', sortable: true, searchable: false, align: 'right' },
    {
      id: 'calendar',
      header: 'Calendar',
      accessor: (row) => row.calendarStatus ?? '',
      sortable: true,
      searchable: false,
      render: (_v, row) => calendarLabel(row),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <PageHeader
        title="Data Health"
        subtitle="How current each organization's directory information is"
        actions={
          <button
            type="button"
            onClick={exportCsv}
            disabled={visible.length === 0}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <HiDownload className="mr-2 h-4 w-4" />
            Export CSV
          </button>
        }
      />

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Time window"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {WINDOW_OPTIONS.map(option => <option key={option.days} value={option.days}>{option.label}</option>)}
        </select>
        <select
          aria-label="Organization type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">All types</option>
          {types.map(type => <option key={type} value={type}>{TYPE_LABELS[type] ?? type}</option>)}
        </select>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {(Object.keys(STATUS_META) as ActivityStatus[]).map(status => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              aria-pressed={statusFilter === status}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_META[status].className} ${
                statusFilter && statusFilter !== status ? 'opacity-40' : ''
              } ${statusFilter === status ? 'ring-2 ring-offset-1 ring-blue-500 dark:ring-offset-gray-900' : ''}`}
            >
              {STATUS_META[status].label}: {statusCounts[status]}
            </button>
          ))}
        </div>
      </div>

      {thresholds && (
        <ul className="grid gap-1 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-2">
          {statusLegend(thresholds).map(([status, text]) => (
            <li key={status}><strong className="text-gray-700 dark:text-gray-300">{STATUS_META[status].label}:</strong> {text}.</li>
          ))}
          <li className="sm:col-span-2">
            "Own users" means the organization's people; edits by AIU staff are counted separately.
            A contact counts as not updated when nobody has changed it in {Math.round(thresholds.staleContactDays / 30.44)} months.
          </li>
        </ul>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : (
        <DataTable
          data={visible}
          columns={columns}
          searchable={true}
          searchPlaceholder="Search organizations..."
          sortable={true}
          pagination={true}
          pageSize={50}
          pageSizeOptions={[25, 50, 100]}
          loading={loading}
          emptyMessage="No organizations match these filters."
          ariaLabel="Data health by organization"
        />
      )}
    </div>
  );
};

export default DataHealthPage;
